/**
 * Field-tech "live job" state.
 *
 * Tracks which sales order the tech is currently working, the screen they're
 * on in the my-day → on-way → on-site → sign → complete flow, and any local
 * captures (parts, photo count, signature flag) that haven't been pushed to
 * Spire yet.
 *
 * Persists to sessionStorage so a mid-job refresh resumes correctly.
 * Production behavior should later persist the timer state across browser
 * restarts via IndexedDB — sessionStorage is adequate for the field-tech
 * PWA's typical sandboxed lifecycle.
 */

import { create } from 'zustand';

export type FieldScreen = 'day' | 'onway' | 'onsite' | 'sign' | 'complete';

export interface FieldPartLocal {
  /** Stable per-job id so React lists stay keyed. */
  clientId: string;
  partNo: string;
  description: string;
  /** Warehouse to source the part from — defaults to the order's customer whse. */
  whse: string;
  /** Decimal as string. */
  qty: string;
  /** Decimal as string. */
  unitPrice: string;
  /** UOM (e.g. 'EA', 'LB'). */
  uom: string;
  /** Sync state for the Spire write. */
  syncStatus: 'pending' | 'syncing' | 'synced' | 'failed';
  /** Spire's line id once the write succeeded. */
  spireLineId: number | null;
  /** Spire's assigned sequence within the order. */
  spireSequence: number | null;
  /** Last error message when syncStatus === 'failed'. */
  lastError: string | null;
}

export interface FieldPhotoLocal {
  clientId: string;
  /** Filename Spire stores for the CRM-note attachment. */
  filename: string;
  /** image/png or image/jpeg. */
  contentType: 'image/png' | 'image/jpeg';
  /** Raw base64 (no data: prefix). Bounded by Spire's attachment size. */
  base64: string;
  /** Optional caption shown alongside the photo and stored in the CRM note body. */
  caption: string;
  /** Approximate KB of the encoded payload. */
  sizeKb: number;
  /** Sync state for the Spire write. */
  syncStatus: 'pending' | 'syncing' | 'synced' | 'failed';
  /** Spire CRM-note id once the write succeeded. */
  spireNoteId: number | null;
  /** Last error message when syncStatus === 'failed'. */
  lastError: string | null;
  /** ms epoch — when the tech snapped the photo. */
  capturedAt: number;
}

export interface FieldJobState {
  screen: FieldScreen;
  currentOrderId: number | null;
  currentOrderNo: string | null;
  /** ms epoch — when the tech tapped "I've arrived." */
  onsiteStartedAt: number | null;
  /** Local-only — these don't write to Spire yet. */
  parts: FieldPartLocal[];
  /** Photos captured this visit. Each entry tracks its own Spire sync. */
  photos: FieldPhotoLocal[];
  /** True once the customer has signed (writes via attachSignature elsewhere). */
  signed: boolean;
  /** Last completed order's resolved totals — held briefly for the complete screen. */
  completeSummary: {
    orderNo: string;
    customerName: string;
    elapsedSec: number;
    partsCount: number;
    photoCount: number;
    total: string;
    /** ms epoch the tech clocked out — drives the Day "Last completed" pill TTL. */
    completedAt: number;
  } | null;

  // ─── actions ──────────────────────────────────────────────────────────
  startJob(orderId: number, orderNo: string): void;
  /**
   * Resume an already-active job without clearing local parts/photos/signed.
   * Called when the tech taps their own live card from Day after parking
   * (the server entry is still open; this just re-flips local `screen`).
   */
  resumeActive(orderId: number, orderNo: string, screen: 'onway' | 'onsite'): void;
  /**
   * Park the current job — flip back to Day without closing the server entry
   * or wiping local visit state. Used by the Onsite Back arrow so a tech can
   * peek their day list and come back to the same in-progress visit.
   */
  parkJob(): void;
  arrive(): void;
  goSign(): void;
  goOnsite(): void;
  addPart(
    part: Omit<
      FieldPartLocal,
      'clientId' | 'syncStatus' | 'spireLineId' | 'spireSequence' | 'lastError'
    >,
  ): string;
  updatePart(clientId: string, patch: Partial<FieldPartLocal>): void;
  removePart(clientId: string): void;
  addPhoto(
    photo: Omit<
      FieldPhotoLocal,
      'clientId' | 'syncStatus' | 'spireNoteId' | 'lastError' | 'capturedAt'
    >,
  ): string;
  updatePhoto(clientId: string, patch: Partial<FieldPhotoLocal>): void;
  removePhoto(clientId: string): void;
  markSigned(): void;
  complete(summary: NonNullable<FieldJobState['completeSummary']>): void;
  cancelJob(): void;
  reset(): void;
}

const STORAGE_KEY = 'tinsmith.field-job';

interface PersistedShape {
  screen: FieldScreen;
  currentOrderId: number | null;
  currentOrderNo: string | null;
  onsiteStartedAt: number | null;
  parts: FieldPartLocal[];
  photos: FieldPhotoLocal[];
  signed: boolean;
  completeSummary: FieldJobState['completeSummary'];
}

function loadPersisted(): PersistedShape | null {
  if (typeof window === 'undefined' || !window.sessionStorage) return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PersistedShape;
  } catch {
    return null;
  }
}

function savePersisted(state: PersistedShape): void {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // sessionStorage full / unavailable — ignore.
  }
}

const initial = loadPersisted();

function clientId(): string {
  return `p_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export const useFieldJob = create<FieldJobState>()((set, get) => {
  const persist = () => {
    const { screen, currentOrderId, currentOrderNo, onsiteStartedAt, parts, photos, signed, completeSummary } = get();
    // Don't persist photo bytes — base64 of a single 2MP image is ~3MB and
    // sessionStorage caps at ~5MB. Strip on persist; the in-memory store
    // keeps the bytes for the active session. A mid-upload refresh loses
    // pending photos (rare, acceptable).
    const photosForPersist = photos.map((p) => ({ ...p, base64: '' }));
    savePersisted({
      screen,
      currentOrderId,
      currentOrderNo,
      onsiteStartedAt,
      parts,
      photos: photosForPersist,
      signed,
      completeSummary,
    });
  };

  return {
    screen: initial?.screen ?? 'day',
    currentOrderId: initial?.currentOrderId ?? null,
    currentOrderNo: initial?.currentOrderNo ?? null,
    onsiteStartedAt: initial?.onsiteStartedAt ?? null,
    parts: initial?.parts ?? [],
    photos: initial?.photos ?? [],
    signed: initial?.signed ?? false,
    completeSummary: initial?.completeSummary ?? null,

    startJob(orderId, orderNo) {
      set({
        screen: 'onway',
        currentOrderId: orderId,
        currentOrderNo: orderNo,
        onsiteStartedAt: null,
        parts: [],
        photos: [],
        signed: false,
        completeSummary: null,
      });
      persist();
    },
    resumeActive(orderId, orderNo, screen) {
      const { currentOrderId } = get();
      // If we're resuming a different job than we have local state for,
      // wipe the visit-only fields (parts/photos/signed) so we don't show
      // a previous order's captures on the new one. Same job = keep them.
      if (currentOrderId !== null && currentOrderId !== orderId) {
        set({
          screen,
          currentOrderId: orderId,
          currentOrderNo: orderNo,
          onsiteStartedAt: screen === 'onsite' ? Date.now() : null,
          parts: [],
          photos: [],
          signed: false,
          completeSummary: null,
        });
      } else {
        set({
          screen,
          currentOrderId: orderId,
          currentOrderNo: orderNo,
          // Stamp onsiteStartedAt the first time we land on onsite so the
          // legacy local elapsed counter has something to anchor on.
          onsiteStartedAt: screen === 'onsite'
            ? get().onsiteStartedAt ?? Date.now()
            : get().onsiteStartedAt,
        });
      }
      persist();
    },
    parkJob() {
      // Flip to Day without touching currentOrderId / parts / photos /
      // signed. The server-side TimeEntry stays open; reconciliation
      // sees ids match and leaves us on Day.
      set({ screen: 'day' });
      persist();
    },
    arrive() {
      set({ screen: 'onsite', onsiteStartedAt: Date.now() });
      persist();
    },
    goSign() {
      set({ screen: 'sign' });
      persist();
    },
    goOnsite() {
      set({ screen: 'onsite' });
      persist();
    },
    addPart(part) {
      const id = clientId();
      set({
        parts: [
          ...get().parts,
          {
            ...part,
            clientId: id,
            syncStatus: 'pending',
            spireLineId: null,
            spireSequence: null,
            lastError: null,
          },
        ],
      });
      persist();
      return id;
    },
    updatePart(id, patch) {
      set({
        parts: get().parts.map((p) => (p.clientId === id ? { ...p, ...patch } : p)),
      });
      persist();
    },
    removePart(id) {
      set({ parts: get().parts.filter((p) => p.clientId !== id) });
      persist();
    },
    addPhoto(photo) {
      const id = clientId();
      set({
        photos: [
          ...get().photos,
          {
            ...photo,
            clientId: id,
            syncStatus: 'pending',
            spireNoteId: null,
            lastError: null,
            capturedAt: Date.now(),
          },
        ],
      });
      persist();
      return id;
    },
    updatePhoto(id, patch) {
      set({
        photos: get().photos.map((p) => (p.clientId === id ? { ...p, ...patch } : p)),
      });
      persist();
    },
    removePhoto(id) {
      set({ photos: get().photos.filter((p) => p.clientId !== id) });
      persist();
    },
    markSigned() {
      set({ signed: true });
      persist();
    },
    complete(summary) {
      set({ screen: 'complete', completeSummary: summary });
      persist();
    },
    cancelJob() {
      set({
        screen: 'day',
        currentOrderId: null,
        currentOrderNo: null,
        onsiteStartedAt: null,
        parts: [],
        photos: [],
        signed: false,
      });
      persist();
    },
    reset() {
      set({
        screen: 'day',
        currentOrderId: null,
        currentOrderNo: null,
        onsiteStartedAt: null,
        parts: [],
        photos: [],
        signed: false,
        completeSummary: null,
      });
      persist();
    },
  };
});

export function formatElapsed(sec: number): string {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) {
    return `${String(h)}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}
